import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { useAuthStore, restoreSession, type AuthState } from '@zendia/frontend-shared'

import './index.css'

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const queryClient = new QueryClient()
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  context: {
    queryClient,
    auth: undefined! as AuthState,
  },
})

export const MainApp = () => {
  const auth = useAuthStore()
  return <RouterProvider router={router} context={{ queryClient, auth }} />
}

/**
 * Keys these values used to be persisted under. Anyone who used the app before they moved
 * still has them sitting in `localStorage`, where the point of moving them was that they
 * should not be, so boot clears them once. Safe to delete this and the call below once no
 * active browser can still be carrying them.
 */
const LEGACY_PERSISTED_KEYS = ['authToken', 'authType', 'verificationToken']

function purgeLegacyPersistedAuth() {
  try {
    LEGACY_PERSISTED_KEYS.forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // Blocked site data throws rather than no-opping; there is nothing to clean up then
  }
}

/** Holds the page during the session exchange, so boot does not look like a blank screen. */
const BootSplash = () => (
  <div className="h-dvh flex items-center justify-center">
    <div className="animate-pulse font-serif font-semibold text-2xl">ZENDIA</div>
  </div>
)

/**
 * The access token lives in memory only, so every load starts unauthenticated and the
 * refresh cookie is the only thing that remembers the session. That exchange has to finish
 * before the router mounts: the authenticated guard reads the token synchronously in
 * `beforeLoad` and would bounce the user to the login form while the request was still in
 * flight.
 *
 * The result is not checked - an unrestored session simply renders as signed out, which the
 * routes already handle.
 */
async function bootstrap() {
  purgeLegacyPersistedAuth()

  const root = createRoot(document.getElementById('root')!)
  root.render(<BootSplash />)

  await restoreSession(import.meta.env.VITE_API_BASE_URL)

  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <MainApp />
      </QueryClientProvider>
    </StrictMode>,
  )
}

void bootstrap()
